import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Upload, X, Eye, Plus, Image } from 'lucide-react';

const FONTS = [
  { value: 'font-sans', label: 'Sans Serif (Default)' },
  { value: 'font-serif', label: 'Serif' },
  { value: 'font-mono', label: 'Monospace' },
  { value: 'font-outfit', label: 'Outfit' },
  { value: 'font-manrope', label: 'Manrope' },
];

const BORDER_RADIUS = [
  { value: 'rounded-none', label: 'None' },
  { value: 'rounded-sm', label: 'Small' },
  { value: 'rounded-md', label: 'Medium' },
  { value: 'rounded-lg', label: 'Large' },
  { value: 'rounded-xl', label: 'Extra Large' },
  { value: 'rounded-2xl', label: '2X Large' },
];

const SHADOWS = [
  { value: 'shadow-none', label: 'None' },
  { value: 'shadow-sm', label: 'Small' },
  { value: 'shadow-md', label: 'Medium' },
  { value: 'shadow-lg', label: 'Large' },
];

const HEADER_DISPLAY_OPTIONS = [
  { value: 'text', label: 'Store Name Only' },
  { value: 'logo', label: 'Logo Only' },
  { value: 'logo_and_text', label: 'Logo + Store Name' },
];

const DEFAULT_THEMES = {
  minimalist: {
    bg_color: '#ffffff',
    text_color: '#09090b',
    accent_color: '#18181b',
    border_color: '#e4e4e7',
    heading_font: 'font-serif',
    body_font: 'font-sans',
    border_radius: 'rounded-none',
    shadow: 'shadow-none',
  },
  hypebeast: {
    bg_color: '#09090b',
    text_color: '#ffffff',
    accent_color: '#a3e635',
    border_color: '#27272a',
    heading_font: 'font-outfit',
    body_font: 'font-sans',
    border_radius: 'rounded-none',
    shadow: 'shadow-none',
  },
  curator: {
    bg_color: '#fffbeb',
    text_color: '#451a03',
    accent_color: '#9f1239',
    border_color: '#fde68a',
    heading_font: 'font-serif',
    body_font: 'font-sans',
    border_radius: 'rounded-sm',
    shadow: 'shadow-sm',
  },
};

export default function ThemeEditor() {
  const navigate = useNavigate();
  const { storeId } = useParams();
  const wallpaperInputRef = useRef(null);
  const logoInputRef = useRef(null);
  
  const [store, setStore] = useState(null);
  const [availableThemes, setAvailableThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Save as new theme dialog
  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');
  
  // Theme state
  const [baseTheme, setBaseTheme] = useState('minimalist');
  const [themeConfig, setThemeConfig] = useState({
    bg_color: '#ffffff',
    text_color: '#09090b',
    accent_color: '#18181b',
    border_color: '#e4e4e7',
    heading_font: 'font-sans',
    body_font: 'font-sans',
    border_radius: 'rounded-md',
    shadow: 'shadow-sm',
    wallpaper: null,
    wallpaper_opacity: 0.5,
  });
  
  // Branding
  const [logoUrl, setLogoUrl] = useState(null);
  const [headerDisplay, setHeaderDisplay] = useState('text');
  
  // Navigation labels
  const [showCollectionsNav, setShowCollectionsNav] = useState(true);
  const [collectionsNavTitle, setCollectionsNavTitle] = useState('Browse Collections');
  const [allProductsLabel, setAllProductsLabel] = useState('All Products');
  
  useEffect(() => {
    fetchStore();
  }, [storeId]);
  
  useEffect(() => {
    if (store) {
      document.title = `Theme - ${store.name}`;
    }
  }, [store]);
  
  const fetchStore = async () => {
    try {
      const [storeRes, themesRes] = await Promise.all([
        fetch(`${API}/stores/${storeId}`, { credentials: 'include' }),
        fetch(`${API}/stores/${storeId}/themes`, { credentials: 'include' }),
      ]);
      
      if (!storeRes.ok) throw new Error('Store not found');
      
      const data = await storeRes.json();
      setStore(data);
      setBaseTheme(data.theme_id || 'minimalist');
      setShowCollectionsNav(data.show_collections_nav !== false);
      setCollectionsNavTitle(data.collections_nav_title || 'Browse Collections');
      setAllProductsLabel(data.all_products_label || 'All Products');
      setLogoUrl(data.logo_url || null);
      setHeaderDisplay(data.header_display || 'text');
      
      // Load available themes
      if (themesRes.ok) {
        const themes = await themesRes.json();
        setAvailableThemes(themes);
      }
      
      // Load theme config or use defaults
      if (data.theme_config) {
        const baseConfig = DEFAULT_THEMES[data.theme_id] || DEFAULT_THEMES.minimalist;
        setThemeConfig({ ...baseConfig, ...data.theme_config });
      } else {
        setThemeConfig(DEFAULT_THEMES[data.theme_id || 'minimalist']);
      }
    } catch (error) {
      toast.error('Failed to load store');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBaseThemeChange = (themeId) => {
    setBaseTheme(themeId);
    
    // Check if it's a custom theme
    const customTheme = availableThemes.find(t => t.theme_id === themeId && t.is_custom);
    if (customTheme && customTheme.config) {
      setThemeConfig(prev => ({
        ...customTheme.config,
        wallpaper: prev.wallpaper,
        wallpaper_opacity: prev.wallpaper_opacity,
      }));
    } else if (DEFAULT_THEMES[themeId]) {
      setThemeConfig(prev => ({
        ...DEFAULT_THEMES[themeId],
        wallpaper: prev.wallpaper,
        wallpaper_opacity: prev.wallpaper_opacity,
      }));
    }
  };
  
  const handleColorChange = (key, value) => {
    setThemeConfig(prev => ({ ...prev, [key]: value }));
  };
  
  const handleImageUpload = async (e, type = 'wallpaper') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (type === 'wallpaper') setUploading(true);
    else setUploadingLogo(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API}/stores/${storeId}/upload-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Upload failed');
      }
      
      const data = await res.json();
      
      if (type === 'wallpaper') {
        setThemeConfig(prev => ({ ...prev, wallpaper: data.url }));
      } else {
        setLogoUrl(data.url);
      }
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      if (type === 'wallpaper') setUploading(false);
      else setUploadingLogo(false);
    }
  };
  
  const handleRemoveWallpaper = () => {
    setThemeConfig(prev => ({ ...prev, wallpaper: null }));
  };
  
  const handleRemoveLogo = () => {
    setLogoUrl(null);
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/stores/${storeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          theme_id: baseTheme,
          theme_config: themeConfig,
          show_collections_nav: showCollectionsNav,
          collections_nav_title: collectionsNavTitle,
          all_products_label: allProductsLabel,
          logo_url: logoUrl,
          header_display: headerDisplay,
        }),
      });
      
      if (!res.ok) throw new Error('Failed to save');
      toast.success('Theme saved!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveAsNewTheme = async () => {
    if (!newThemeName.trim()) {
      toast.error('Please enter a theme name');
      return;
    }
    
    try {
      const res = await fetch(`${API}/custom-themes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newThemeName.trim(),
          config: themeConfig,
        }),
      });
      
      if (!res.ok) throw new Error('Failed to create theme');
      
      const data = await res.json();
      toast.success(`Theme "${newThemeName}" created!`);
      
      // Update the base theme to the new custom theme
      setBaseTheme(data.theme_id);
      
      // Refresh available themes
      const themesRes = await fetch(`${API}/stores/${storeId}/themes`, { credentials: 'include' });
      if (themesRes.ok) {
        setAvailableThemes(await themesRes.json());
      }
      
      setSaveAsDialogOpen(false);
      setNewThemeName('');
      
      // Also save to store
      await handleSave();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Preview styles
  const previewStyle = {
    backgroundColor: themeConfig.bg_color,
    color: themeConfig.text_color,
    backgroundImage: themeConfig.wallpaper ? `url(${themeConfig.wallpaper})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full"
              onClick={() => navigate(`/store/${storeId}/products`)}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-outfit font-bold">Theme Editor</h1>
              <p className="text-muted-foreground">{store?.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {store?.is_published && (
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => window.open(`/s/${store.slug}`, '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
            )}
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setSaveAsDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Save as New Theme
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Theme'}
            </Button>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Editor Panel */}
          <div className="space-y-6">
            {/* Base Theme Selection */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-outfit font-bold mb-4">Base Theme</h2>
              <Select value={baseTheme} onValueChange={handleBaseThemeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableThemes.map((theme) => (
                    <SelectItem key={theme.theme_id} value={theme.theme_id}>
                      {theme.name} {theme.is_custom && '(Custom)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-2">
                Start with a base theme, then customize below
              </p>
            </div>
            
            {/* Store Branding */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-outfit font-bold mb-4">Store Branding</h2>
              <div className="space-y-4">
                <div>
                  <Label>Header Display</Label>
                  <Select value={headerDisplay} onValueChange={setHeaderDisplay}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HEADER_DISPLAY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {(headerDisplay === 'logo' || headerDisplay === 'logo_and_text') && (
                  <div>
                    <Label>Store Logo</Label>
                    {logoUrl ? (
                      <div className="mt-2 flex items-center gap-4">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted border">
                          <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveLogo}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'logo')}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="w-full"
                        >
                          <Image className="w-4 h-4 mr-2" />
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Colors */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-outfit font-bold mb-4">Colors</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Background</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={themeConfig.bg_color}
                      onChange={(e) => handleColorChange('bg_color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={themeConfig.bg_color}
                      onChange={(e) => handleColorChange('bg_color', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Text</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={themeConfig.text_color}
                      onChange={(e) => handleColorChange('text_color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={themeConfig.text_color}
                      onChange={(e) => handleColorChange('text_color', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Accent</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={themeConfig.accent_color}
                      onChange={(e) => handleColorChange('accent_color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={themeConfig.accent_color}
                      onChange={(e) => handleColorChange('accent_color', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Border</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={themeConfig.border_color}
                      onChange={(e) => handleColorChange('border_color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={themeConfig.border_color}
                      onChange={(e) => handleColorChange('border_color', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Typography */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-outfit font-bold mb-4">Typography</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Heading Font</Label>
                  <Select 
                    value={themeConfig.heading_font} 
                    onValueChange={(v) => handleColorChange('heading_font', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONTS.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Body Font</Label>
                  <Select 
                    value={themeConfig.body_font} 
                    onValueChange={(v) => handleColorChange('body_font', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONTS.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Style */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-outfit font-bold mb-4">Style</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Border Radius</Label>
                  <Select 
                    value={themeConfig.border_radius} 
                    onValueChange={(v) => handleColorChange('border_radius', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BORDER_RADIUS.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Shadow</Label>
                  <Select 
                    value={themeConfig.shadow} 
                    onValueChange={(v) => handleColorChange('shadow', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SHADOWS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Wallpaper */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-outfit font-bold mb-4">Wallpaper</h2>
              {themeConfig.wallpaper ? (
                <div className="space-y-4">
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                    <img 
                      src={themeConfig.wallpaper} 
                      alt="Wallpaper" 
                      className="w-full h-full object-cover"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 rounded-full"
                      onClick={handleRemoveWallpaper}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div>
                    <Label>Overlay Opacity: {Math.round(themeConfig.wallpaper_opacity * 100)}%</Label>
                    <Slider
                      value={[themeConfig.wallpaper_opacity * 100]}
                      onValueChange={([v]) => handleColorChange('wallpaper_opacity', v / 100)}
                      max={100}
                      step={5}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Higher opacity makes text more readable over the image
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <input
                    ref={wallpaperInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'wallpaper')}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => wallpaperInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full h-24 border-dashed"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Wallpaper'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Max 5MB. JPEG, PNG, WebP, or GIF
                  </p>
                </div>
              )}
            </div>
            
            {/* Navigation Labels */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-outfit font-bold mb-4">Collection Navigation</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Show Collection Browser</Label>
                    <p className="text-sm text-muted-foreground">
                      Display sidebar for browsing collections
                    </p>
                  </div>
                  <Switch
                    checked={showCollectionsNav}
                    onCheckedChange={setShowCollectionsNav}
                  />
                </div>
                {showCollectionsNav && (
                  <>
                    <div>
                      <Label>Navigation Title</Label>
                      <Input
                        value={collectionsNavTitle}
                        onChange={(e) => setCollectionsNavTitle(e.target.value)}
                        placeholder="Browse Collections"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>All Products Label</Label>
                      <Input
                        value={allProductsLabel}
                        onChange={(e) => setAllProductsLabel(e.target.value)}
                        placeholder="All Products"
                        className="mt-1"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Preview Panel */}
          <div className="lg:sticky lg:top-8 h-fit">
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="p-4 border-b bg-muted/30">
                <h2 className="font-outfit font-bold">Live Preview</h2>
              </div>
              <div 
                className="aspect-[4/3] relative overflow-hidden"
                style={previewStyle}
              >
                {/* Overlay for wallpaper */}
                {themeConfig.wallpaper && (
                  <div 
                    className="absolute inset-0"
                    style={{ 
                      backgroundColor: themeConfig.bg_color,
                      opacity: themeConfig.wallpaper_opacity 
                    }}
                  />
                )}
                
                {/* Preview Content */}
                <div className="relative z-10 p-6 h-full flex flex-col">
                  {/* Header */}
                  <div 
                    className="pb-4 mb-4 flex items-center gap-3"
                    style={{ borderBottom: `1px solid ${themeConfig.border_color}` }}
                  >
                    {(headerDisplay === 'logo' || headerDisplay === 'logo_and_text') && logoUrl && (
                      <img src={logoUrl} alt="Logo" className="h-8 w-8 object-contain" />
                    )}
                    {(headerDisplay === 'text' || headerDisplay === 'logo_and_text') && (
                      <h1 
                        className={`text-2xl font-bold ${themeConfig.heading_font}`}
                        style={{ color: themeConfig.text_color }}
                      >
                        {store?.name || 'Store Name'}
                      </h1>
                    )}
                  </div>
                  
                  {/* Product Cards */}
                  <div className="grid grid-cols-2 gap-4 flex-1">
                    {[1, 2].map(i => (
                      <div 
                        key={i}
                        className={`${themeConfig.border_radius} ${themeConfig.shadow} overflow-hidden`}
                        style={{ 
                          backgroundColor: themeConfig.bg_color,
                          border: `1px solid ${themeConfig.border_color}` 
                        }}
                      >
                        <div className="aspect-square bg-gray-200" />
                        <div className="p-3">
                          <h3 
                            className={`font-semibold text-sm ${themeConfig.heading_font}`}
                            style={{ color: themeConfig.text_color }}
                          >
                            Product {i}
                          </h3>
                          <p 
                            className={`text-xs mt-1 ${themeConfig.body_font}`}
                            style={{ color: themeConfig.text_color, opacity: 0.7 }}
                          >
                            $99.00
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Accent Button */}
                  <div className="mt-4">
                    <button
                      className={`px-4 py-2 text-sm font-medium ${themeConfig.border_radius}`}
                      style={{ 
                        backgroundColor: themeConfig.accent_color,
                        color: themeConfig.bg_color
                      }}
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Save As New Theme Dialog */}
      <Dialog open={saveAsDialogOpen} onOpenChange={setSaveAsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as New Theme</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="theme-name">Theme Name</Label>
            <Input
              id="theme-name"
              value={newThemeName}
              onChange={(e) => setNewThemeName(e.target.value)}
              placeholder="My Custom Theme"
              className="mt-2"
            />
            <p className="text-sm text-muted-foreground mt-2">
              This will save your current customizations as a reusable theme.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveAsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAsNewTheme}>
              Create Theme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
